import { useState, useRef } from "react";
import { useNavigate } from "@reach/router";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { RFX_TABS } from "utils/constants";
import { copyToSuppliers } from "../../../api/clarification-api";
import type {
  Clarification,
  CreateClarificationPayload,
  UpdateClarificationPayload,
  ImportClarificationsResult,
  ValidateImportResult,
  SubmitClarificationResponsePayload,
} from "../../../types/clarification.type";
import { FileableFile } from "types/storage.types";

interface UseModalStateOptions {
  clarifications: Clarification[];
  isClientView: boolean;
  openSupplierOnClick?: boolean;
  rfxId?: number;
  selectedIds: number[];
  addClarification: (payload: CreateClarificationPayload) => Promise<boolean>;
  clearSelection: () => void;
  editClarification: (id: number, payload: UpdateClarificationPayload) => Promise<boolean>;
  handleImport: (rows: any) => Promise<ImportClarificationsResult | undefined>;
  handleValidateImport: (file: File) => Promise<ValidateImportResult | null>;
  refetch: () => Promise<void>;
  removeClarification: (id: number) => Promise<boolean>;
  saveDraftResponse: (id: number, payload: SubmitClarificationResponsePayload) => Promise<boolean>;
  sendDraftClarifications: (ids: number[], dateDue?: string) => Promise<boolean>;
  sendSelectedResponses: (ids: number[]) => Promise<boolean>;
  updateLocalClarifications: (clarifications: Clarification[]) => void;
  onRowClickOverride?: (clarification: Clarification) => void | null;
}

export const useModalState = ({
  rfxId,
  isClientView,
  clarifications,
  addClarification,
  editClarification,
  removeClarification,
  sendDraftClarifications,
  sendSelectedResponses,
  saveDraftResponse,
  handleValidateImport,
  handleImport,
  updateLocalClarifications,
  refetch,
  clearSelection,
  selectedIds,
  openSupplierOnClick,
  onRowClickOverride,
}: UseModalStateOptions) => {
  const navigate = useNavigate();
  const { clientPaths } = useAuthContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClarification, setSelectedClarification] = useState<Clarification | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isSendingResponses, setIsSendingResponses] = useState(false);
  const [importResult, setImportResult] = useState<ImportClarificationsResult | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [deletingClarification, setDeletingClarification] = useState<Clarification | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isSendResponsesModalOpen, setIsSendResponsesModalOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateImportResult | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [copyingClarification, setCopyingClarification] = useState<Clarification | null>(null);
  const [isCopying, setIsCopying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenCreateModal = () => {
    setSelectedClarification(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (clarification: Clarification) => {
    setSelectedClarification(clarification);
    setIsModalOpen(true);
  };

  const handleOpenViewModal = (clarification: Clarification) => {
    setSelectedClarification(clarification);
    setIsModalOpen(true);
  };

  const handleOpenChat = (clarification: Clarification) => {
    setSelectedClarification(clarification);
    setIsModalOpen(true);

    if (clarification.conversation_unread_count && clarification.conversation_unread_count > 0) {
      updateLocalClarifications(
        clarifications.map((c) =>
          c.id === clarification.id ? { ...c, conversation_unread_count: 0 } : c
        )
      );
    }
  };

  const handleCloseModal = (uploadFiles: FileableFile[]) => {
    setIsModalOpen(false);
    setSelectedClarification(null);

    if (uploadFiles && selectedClarification) {
      updateLocalClarifications(
        clarifications.map((c) => c.id === selectedClarification.id ? { ...c, files: uploadFiles } : c)
      );
    }
  };

  const handleSave = async (
    payload: CreateClarificationPayload | UpdateClarificationPayload,
    clarificationId?: number
  ): Promise<boolean> => {
    if (clarificationId) {
      return editClarification(clarificationId, payload as UpdateClarificationPayload);
    }
    return addClarification(payload as CreateClarificationPayload);
  };

  const handleResponseSave = async (
    payload: SubmitClarificationResponsePayload,
    clarificationId?: number
  ): Promise<boolean> => {
    if (!clarificationId) return false;
    return saveDraftResponse(clarificationId, payload);
  };

  const handleDelete = async (clarification: Clarification) => {
    if (!clarification.is_sent) {
      setDeletingClarification(clarification);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingClarification) return;
    setIsDeleting(true);
    try {
      await removeClarification(deletingClarification.id);
      setIsDeleteModalOpen(false);
      setDeletingClarification(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSendSelected = async () => {
    if (selectedIds.length === 0) return;
    if (isClientView) {
      setIsSendModalOpen(true);
    } else {
      setIsSendResponsesModalOpen(true);
    }
  };

  const handleConfirmSend = async (dateDue?: string) => {
    setIsSending(true);
    try {
      const success = isClientView
        ? await sendDraftClarifications(selectedIds, dateDue)
        : await sendSelectedResponses(selectedIds);
      if (success) {
        clearSelection();
        if (isClientView) {
          setIsSendModalOpen(false);
        } else {
          setIsSendResponsesModalOpen(false);
        }
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const result = await handleValidateImport(file);
      if (result) {
        setValidationResult(result);
        setIsValidationModalOpen(true);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConfirmImport = async (rows: any) => {
    const result = await handleImport(rows);
    if (result && result.errors.length === 0) {
      setIsValidationModalOpen(false);
      setValidationResult(null);
    }
    return result;
  };

  const handleCopyToSuppliers = async (clarificationId: number, rfxInviteIds: number[]) => {
    setIsCopying(true);
    try {
      const result = await copyToSuppliers(clarificationId, rfxInviteIds);
      if (result) {
        setCopyingClarification(null);
        await refetch();
      }
    } finally {
      setIsCopying(false);
    }
  };

  const handleSupplierNameClick = (submissionId: number) => {
    navigate(clientPaths?.viewRfxSubmission(rfxId, submissionId, RFX_TABS.CLARIFICATIONS));
  };

  const handleRowClick = (clarification: Clarification) => {
    if (onRowClickOverride) {
      const result = onRowClickOverride(clarification);
      if (result !== null) return;
    }

    if (isClientView) {
      return clarification.is_sent ? handleOpenViewModal(clarification) : handleOpenEditModal(clarification);
    }

    return !clarification.final_response ? handleOpenViewModal(clarification) : handleOpenEditModal(clarification);
  };

  return {
    isModalOpen,
    selectedClarification,
    isSending,
    isSendingResponses,
    importResult,
    isImportModalOpen,
    setIsImportModalOpen,
    deletingClarification,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    setDeletingClarification,
    isDeleting,
    isSendModalOpen,
    setIsSendModalOpen,
    isSendResponsesModalOpen,
    setIsSendResponsesModalOpen,
    validationResult,
    isValidationModalOpen,
    setIsValidationModalOpen,
    setValidationResult,
    copyingClarification,
    setCopyingClarification,
    isCopying,
    fileInputRef,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleOpenViewModal,
    handleOpenChat,
    handleCloseModal,
    handleSave,
    handleResponseSave,
    handleDelete,
    handleConfirmDelete,
    handleSendSelected,
    handleConfirmSend,
    handleFileSelect,
    handleConfirmImport,
    handleCopyToSuppliers,
    handleSupplierNameClick,
    handleRowClick,
    openSupplierOnClick,
  };
};

export type ModalStateReturn = ReturnType<typeof useModalState>;
